import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useDateUrl — sync a YYYY-MM-DD URL query param with a page's date state.
 *
 * Why a hook (not a component or context):
 *   Each scheduling surface owns its own "current date" state — Dispatch has
 *   `dispatchDate`, Calendar has `anchorDate`, Forecast has `dateFrom`. We
 *   don't want to lift those into a shared parent or duplicate the picker
 *   logic. The hook adds URL persistence in three lines per page.
 *
 * Contract:
 *   - `initialDate`  → Date parsed from `?date=` on first render, or null if
 *                      absent / invalid. Pages seed their own state with this.
 *   - `syncDate(d)`  → push `?date=YYYY-MM-DD` to the URL. No-op if the param
 *                      already matches. Memoized so consumers can drop it
 *                      directly into useEffect deps without re-firing.
 *
 * Browser Back/Forward "just works" because react-router-dom's
 * setSearchParams pushes a history entry by default. The user steps through
 * their date history one entry at a time.
 *
 * Notes:
 *   - LOCAL date components are used both ways (read and write). This stays
 *     in lockstep with the rest of the date-handling architecture which is
 *     already local-centric since iter-254.
 *   - The hook is intentionally permissive on read (rejects only invalid
 *     formats) so a stray non-date param like `?date=foo` doesn't crash.
 */
/**
 * useDateUrl — bi-directional sync between a YYYY-MM-DD URL query param
 * and a page's date state.
 *
 * Returns:
 *   urlDateYmd : string|null — the current ?date= value parsed and validated.
 *                              Stable across renders (string compare). Use
 *                              this in effect dep arrays to react to Back /
 *                              Forward without spawning a new Date each render.
 *   urlDate    : Date|null  — convenience Date built from urlDateYmd, OR null.
 *   syncDate   : (Date) => void — push the date back into the URL. Memoized
 *                              and no-ops if the param already matches.
 *
 * Pattern in a page:
 *   const { urlDateYmd, urlDate, syncDate } = useDateUrl();
 *   const [date, setDate] = useState(() => urlDate || new Date());
 *   // Pull: react to Back/Forward
 *   useEffect(() => {
 *     if (urlDateYmd && urlDateYmd !== ymdLocal(date)) setDate(parseYmd(urlDateYmd));
 *   }, [urlDateYmd]);
 *   // Push: react to user-driven state changes
 *   useEffect(() => { syncDate(date); }, [date, syncDate]);
 */
export function useDateUrl(paramKey = 'date') {
  const [params, setParams] = useSearchParams();

  const raw = params.get(paramKey);
  const urlDateYmd =
    raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;

  const urlDate = (() => {
    if (!urlDateYmd) return null;
    const [y, m, d] = urlDateYmd.split('-').map(Number);
    const candidate = new Date(y, m - 1, d, 0, 0, 0, 0);
    return Number.isNaN(candidate.getTime()) ? null : candidate;
  })();

  const syncDate = useCallback(
    (date) => {
      if (!date || Number.isNaN(date.getTime?.())) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(paramKey);
            return next;
          },
          { replace: true },
        );
        return;
      }
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const val = `${y}-${m}-${d}`;
      setParams(
        (prev) => {
          if (prev.get(paramKey) === val) return prev;
          const next = new URLSearchParams(prev);
          next.set(paramKey, val);
          return next;
        },
        { replace: false }, // push — back/forward steps through dates
      );
    },
    [paramKey, setParams],
  );

  return { urlDateYmd, urlDate, syncDate, initialDate: urlDate };
}
